import { Component, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { FileUpload, FileUploadModule } from 'primeng/fileupload';
import { NGXLogger } from 'ngx-logger';
import { MessageService } from 'primeng/api';
import { ZammadService } from '../../../core/services/agriflow-api/zammad.service';

export interface SupportTicketData {
  userName: string;
  userEmail: string;
}

@Component({
  selector: 'app-support-ticket-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ButtonModule,
    InputTextModule,
    InputTextareaModule,
    FileUploadModule,
  ],
  templateUrl: './support-ticket-dialog.component.html',
  styleUrls: ['./support-ticket-dialog.component.scss'],
})
export class SupportTicketDialogComponent implements OnInit {
  @ViewChild('fileUpload') fileUpload!: FileUpload;

  ticketForm!: FormGroup;
  isSubmitting = false;
  uploadedFiles: File[] = [];

  constructor(
    private fb: FormBuilder,
    private ref: DynamicDialogRef,
    private config: DynamicDialogConfig,
    private logger: NGXLogger,
    private messageService: MessageService,
    private zammadService: ZammadService,
  ) {}

  ngOnInit(): void {
    const userData = this.config.data as SupportTicketData;

    this.zammadService.getTickets().subscribe((tickets) => {
      this.logger.info('[SUPPORT TICKET] Tickets trovati:', tickets);
    });

    this.ticketForm = this.fb.group({
      name: [userData?.userName || '', [Validators.required]],
      email: [
        userData?.userEmail || '',
        [Validators.required, Validators.email],
      ],
      subject: ['', [Validators.required, Validators.minLength(3)]],
      message: ['', [Validators.required, Validators.minLength(10)]],
    });

    this.logger.info('[SUPPORT TICKET] Dialog aperto con dati:', userData);
  }

  onFileSelect(event: any): void {
    for (const file of event.files) {
      this.uploadedFiles.push(file);
    }
    this.logger.info(
      '[SUPPORT TICKET] File caricati:',
      this.uploadedFiles.length,
    );
  }

  onFileRemove(event: any): void {
    const index = this.uploadedFiles.indexOf(event.file);
    if (index > -1) {
      this.uploadedFiles.splice(index, 1);
    }
    this.logger.info(
      '[SUPPORT TICKET] File rimosso. Totale:',
      this.uploadedFiles.length,
    );
  }

  openFileDialog(): void {
    // Triggera il click sull'input file nascosto del componente p-fileUpload
    const advancedFileInput = this.fileUpload?.advancedFileInput?.nativeElement;
    if (advancedFileInput) {
      advancedFileInput.click();
    }
  }

  onSubmit(): void {
    if (this.ticketForm.invalid) {
      Object.keys(this.ticketForm.controls).forEach((key) => {
        const control = this.ticketForm.get(key);
        if (control?.invalid) {
          control.markAsTouched();
        }
      });
      return;
    }

    this.isSubmitting = true;
    this.logger.info('[SUPPORT TICKET] Invio ticket in corso...');

    const name = this.ticketForm.get('name')?.value;
    const email = this.ticketForm.get('email')?.value;
    const subject = this.ticketForm.get('subject')?.value;
    const message = this.ticketForm.get('message')?.value;

    // Chiama il servizio Zammad per creare il ticket
    this.zammadService
      .createTicket(name, email, subject, message, this.uploadedFiles)
      .subscribe({
        next: (response) => {
          this.logger.info(
            '[SUPPORT TICKET] Ticket creato con successo:',
            response,
          );

          this.messageService.add({
            severity: 'success',
            summary: 'Ticket Creato',
            detail: `La tua richiesta di supporto #${response.number} è stata inviata con successo. Ti contatteremo appena possibile!`,
            life: 6000,
          });

          this.ref.close({ success: true, ticketNumber: response.number });
          this.isSubmitting = false;
        },
        error: (error) => {
          this.logger.error(
            '[SUPPORT TICKET] Errore durante invio ticket:',
            error,
          );

          let errorMessage =
            "Si è verificato un errore durante l'invio del ticket. Riprova più tardi.";

          // Gestione errori specifici
          if (error.status === 401) {
            errorMessage =
              'Errore di autenticazione con il sistema di ticketing.';
          } else if (error.status === 422) {
            errorMessage =
              'Dati del ticket non validi. Verifica i campi inseriti.';
          } else if (error.status === 0) {
            errorMessage =
              'Impossibile connettersi al server di ticketing. Verifica la connessione.';
          }

          this.messageService.add({
            severity: 'error',
            summary: 'Errore',
            detail: errorMessage,
            life: 6000,
          });

          this.isSubmitting = false;
        },
      });
  }

  onCancel(): void {
    this.ref.close({ success: false });
  }

  // Getter per accesso facile ai controlli del form
  get name() {
    return this.ticketForm.get('name');
  }

  get email() {
    return this.ticketForm.get('email');
  }

  get subject() {
    return this.ticketForm.get('subject');
  }

  get message() {
    return this.ticketForm.get('message');
  }
}
